<template>
  <div class="space-y-6">
    <!-- Action Buttons -->
    <div class="flex flex-wrap gap-3">
      <button
        type="button"
        class="btn btn--primary-blue flex items-center space-x-2"
        data-test="add-supplier"
        @click.prevent="addSupplier"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

        <span>{{ $t('ProjectSettings.CompanyTab.CompanySupplierList.buttons.addVendor') }}</span>
      </button>
    </div>

    <!-- Table Container -->
    <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div
        v-if="projectSuppliers.length"
        class="overflow-x-auto"
      >
        <table class="w-full text-sm">
          <thead class="bg-gray-50 border-b border-gray-200">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>

              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Address
              </th>

              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact Name
              </th>

              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact Email
              </th>

              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact Phone
              </th>
            </tr>
          </thead>

          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="(projectSupplier) in projectSuppliers"
              :key="projectSupplier.id"
              class="hover:bg-gray-50 transition-colors duration-150"
            >
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center space-x-3">
                  <div class="flex items-center space-x-2">
                    <button
                      type="button"
                      class="btn-icon btn-icon-red"
                      @click="removeSupplier(projectSupplier.id)"
                    >
                      <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-5 h-5"
                        data-test="trash">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
                    </button>

                    <button
                      type="button"
                      class="btn-icon btn-icon-blue"
                      data-test="edit"
                      @click="editSupplier(projectSupplier)"
                    >
                      <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-5 h-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
                    </button>
                  </div>

                  <span
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium"
                    :class="[colourForType(projectSupplier.type)]"
                  >
                    {{ supplierTypes.find((types) => types.value === projectSupplier.type)?.label }}
                  </span>

                  <span class="text-sm font-medium text-gray-900">
                    {{ projectSupplier.supplier?.name }}
                  </span>
                </div>
              </td>

              <td class="px-6 py-4 text-sm text-gray-900">
                {{ projectSupplier.address || '-' }}
              </td>

              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ projectSupplier.contact_name || '-' }}
              </td>

              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ projectSupplier.contact_email || '-' }}
              </td>

              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ projectSupplier.contact_number || '-' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-else
        class="text-center py-12"
      >
        <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="mx-auto h-12 w-12 text-gray-400">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
    />
</svg>

        <h3 class="mt-2 text-sm font-medium text-gray-900">
          {{ $t('ProjectSettings.CompanyTab.CompanySupplierList.messages.noVendorsFound') }}
        </h3>

        <p class="mt-1 text-sm text-gray-500">
          Get started by adding a new vendor to your company.
        </p>
      </div>
    </div>

    <ProjectSupplierModal
      v-if="show"
      :project-supplier="selectedProjectSupplier"
      :company-id="companyId"
      @close="show = false"
    />
  </div>
</template>

<script setup lang="ts">
import { t } from '@/i18n';
import ProjectSupplier, { SupplierType } from '@/models/ProjectSupplier';
import { useCompanyProjectSupplierStore } from '@/store/companyProjectSupplier';
import { useProjectSupplierStore } from '@/store/projectSupplier';
import Swal from 'sweetalert2';
import { computed, ref } from 'vue';
import ProjectSupplierModal from './CompanyProjectSupplierModal.vue';

const props = defineProps<{
  companyId: number;
}>();

const projectSuppliersStore = useCompanyProjectSupplierStore();

const removeSupplier = (id: Id) => {
  Swal.fire({
    icon: 'warning',
    title: t('ProjectSettings.CompanyTab.CompanySupplierList.messages.removeVendorTitle'),
    text: t('ProjectSettings.CompanyTab.CompanySupplierList.messages.removeVendorText'),
    showConfirmButton: true,
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      projectSuppliersStore.deleteProjectSupplier(id).catch((error) => {
        if(error.status === 422) {
          Swal.fire({
            icon: 'error',
            title: t('ProjectSettings.CompanyTab.CompanySupplierList.messages.deleteFailedTitle'),
            text: error.data.message,
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: t('ProjectSettings.CompanyTab.CompanySupplierList.messages.deleteFailedTitle'),
          });
        }
      });
    }
  });
};

const show = ref(false);
const selectedProjectSupplier = ref();

const editSupplier = (projectSupplier: ProjectSupplier) => {
  selectedProjectSupplier.value = projectSupplier;
  show.value = true;
};

const projectSuppliers = computed(() => {
  return projectSuppliersStore.models.where('company_id', props.companyId).with('supplier').get();
});

const addSupplier = () => {
  selectedProjectSupplier.value = undefined;
  show.value = true;
};

const supplierTypes = computed(() => {
  const types = [];

  for(const type in SupplierType) {
    const value = SupplierType[type];

    types.push({ value, label: type.replace('_', ' ') });
  }

  return types;
});

const colourForType = (type: ObjectValues<typeof SupplierType>) => {
  switch (type) {
    case SupplierType.labour:
      return 'bg-ccm-blue-500 text-white';

    case SupplierType.material:
      return 'bg-green-500 text-white';

    case SupplierType.plant:
      return 'bg-orange-500 text-white';

    default:
      return 'bg-ccm-blue-500 text-white';
  }
};
</script>

<style scoped></style>
